import type { JSONContent } from "@tiptap/core";
import { mergeAttributes } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import Mention from "@tiptap/extension-mention";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import type { SuggestionProps } from "@tiptap/suggestion";
import { useEffect, useRef, useState } from "react";
import { docJSONToText, textToDocJSON } from "./mailTemplateText";
import {
  SCHEDULE_MAIL_PARAMETER_KIND_LABELS,
  SCHEDULE_MAIL_PARAMETER_KIND_ORDER,
  type ScheduleMailParameterDefinition,
  type ScheduleMailParameterKind,
} from "./scheduleMailTemplate";

interface MailTemplateEditorProps {
  value: string;
  onChange: (text: string) => void;
  /** このエディタで挿入可能なパラメータ（クライアント向け / スポンサー向けで異なる） */
  parameters: ScheduleMailParameterDefinition[];
  /** 件名用: 改行を許可しない */
  singleLine?: boolean;
}

interface SuggestionState {
  items: ScheduleMailParameterDefinition[];
  rect: DOMRect | null;
  selectedIndex: number;
  command: (item: { id: string; label: string }) => void;
}

function buildExtensions(
  singleLine: boolean,
  parameters: ScheduleMailParameterDefinition[],
  setSuggestion: (s: SuggestionState | null) => void,
) {
  const suggestionStateRef = { current: null as SuggestionState | null };

  return [
    singleLine ? Document.extend({ content: "paragraph" }) : Document,
    Paragraph,
    Text,
    Mention.configure({
      HTMLAttributes: { class: "mail-parameter-chip" },
      renderText: ({ node }) => `@${node.attrs.label}`,
      renderHTML: ({ options, node }) => [
        "span",
        mergeAttributes(options.HTMLAttributes),
        `@${node.attrs.label}`,
      ],
      suggestion: {
        char: "@",
        items: ({ query }: { query: string }) =>
          parameters.filter((p) => p.name.startsWith(query.toLowerCase())),
        // デフォルトはトークン挿入後に半角スペースを付けるが、
        // 「@system_interviewee_typeとして」のように直後に文字が続くケースが多いため付けない
        command: ({ editor, range, props }) => {
          editor
            .chain()
            .focus()
            .insertContentAt(range, [{ type: "mention", attrs: props }])
            .run();
        },
        render: () => {
          const update = (
            props: SuggestionProps<ScheduleMailParameterDefinition>,
          ) => {
            const prev = suggestionStateRef.current;
            const state: SuggestionState = {
              items: props.items,
              rect: props.clientRect?.() ?? null,
              selectedIndex: prev ? prev.selectedIndex : 0,
              command: (item) => props.command(item),
            };
            suggestionStateRef.current = state;
            setSuggestion(state);
          };
          return {
            onStart: update,
            onUpdate: update,
            onKeyDown: ({ event }: { event: KeyboardEvent }) => {
              const state = suggestionStateRef.current;
              if (!state || state.items.length === 0) return false;
              if (event.key === "ArrowDown") {
                const next = {
                  ...state,
                  selectedIndex: (state.selectedIndex + 1) % state.items.length,
                };
                suggestionStateRef.current = next;
                setSuggestion(next);
                return true;
              }
              if (event.key === "ArrowUp") {
                const next = {
                  ...state,
                  selectedIndex:
                    (state.selectedIndex - 1 + state.items.length) %
                    state.items.length,
                };
                suggestionStateRef.current = next;
                setSuggestion(next);
                return true;
              }
              if (event.key === "Enter") {
                const item = state.items[state.selectedIndex];
                if (item) state.command({ id: item.name, label: item.name });
                return true;
              }
              if (event.key === "Escape") {
                suggestionStateRef.current = null;
                setSuggestion(null);
                return true;
              }
              return false;
            },
            onExit: () => {
              suggestionStateRef.current = null;
              setSuggestion(null);
            },
          };
        },
      },
    }),
  ];
}

export function MailTemplateEditor({
  value,
  onChange,
  parameters,
  singleLine = false,
}: MailTemplateEditorProps) {
  const [suggestion, setSuggestion] = useState<SuggestionState | null>(null);
  // buildExtensions は初回のみ構築する（useRef に直接呼び出しを渡すと毎レンダー実行され結果が捨てられる）。
  // singleLine は件名=常にtrue / 本文=常にfalse、parameters はエディタ単位で固定運用のため初回値で確定してよい。
  const extensionsRef = useRef<ReturnType<typeof buildExtensions> | null>(null);
  if (extensionsRef.current === null) {
    extensionsRef.current = buildExtensions(
      singleLine,
      parameters,
      setSuggestion,
    );
  }

  const editor = useEditor({
    extensions: extensionsRef.current,
    content: textToDocJSON(value, parameters),
    onUpdate: ({ editor: currentEditor }) => {
      onChange(
        docJSONToText(currentEditor.getJSON() as JSONContent, parameters),
      );
    },
  });

  // 外部から value が変わった場合（ユースケース切替によるリセット等）に反映する。
  // 編集中の onChange 往復では書き換えない（カーソル位置が壊れるため）
  useEffect(() => {
    if (!editor) return;
    const currentText = docJSONToText(
      editor.getJSON() as JSONContent,
      parameters,
    );
    if (currentText !== value) {
      editor.commands.setContent(textToDocJSON(value, parameters));
    }
  }, [editor, value, parameters]);

  return (
    <>
      <EditorContent
        editor={editor}
        className={`mail-template-editor w-full rounded border border-[var(--color-border-default)] p-2 text-sm ${
          singleLine ? "" : "min-h-[240px]"
        }`}
      />
      {suggestion?.rect && suggestion.items.length > 0 && (
        <div
          className="fixed z-50 w-[300px] overflow-hidden rounded-lg border border-[var(--color-border-default)] bg-white text-xs shadow-lg"
          style={{
            // 画面右端で @ 入力時にドロップダウン(300px)がビューポート外へ溢れないようクランプ
            left: Math.min(suggestion.rect.left, window.innerWidth - 300 - 8),
            top: suggestion.rect.bottom + 4,
          }}
        >
          {SCHEDULE_MAIL_PARAMETER_KIND_ORDER.map((kind) => (
            <SuggestionGroup
              key={kind}
              title={SCHEDULE_MAIL_PARAMETER_KIND_LABELS[kind]}
              kind={kind}
              state={suggestion}
            />
          ))}
        </div>
      )}
    </>
  );
}

function SuggestionGroup({
  title,
  kind,
  state,
}: {
  title: string;
  kind: ScheduleMailParameterKind;
  state: SuggestionState;
}) {
  const items = state.items.filter((p) => p.kind === kind);
  if (items.length === 0) return null;
  return (
    <>
      <div className="px-2 pt-1.5 pb-0.5 font-bold text-[10px] text-[var(--color-text-secondary)]">
        {title}
      </div>
      {items.map((parameter) => {
        const isSelected =
          state.items[state.selectedIndex]?.name === parameter.name;
        return (
          <button
            key={parameter.name}
            type="button"
            className={`flex w-full items-center justify-between px-2 py-1 text-left ${
              isSelected ? "bg-[var(--color-primary-50)]" : ""
            }`}
            onMouseDown={(e) => {
              e.preventDefault();
              state.command({ id: parameter.name, label: parameter.name });
            }}
          >
            <span
              className={`font-mono text-[11px] ${
                parameter.kind === "system"
                  ? "text-[var(--color-primary-500)]"
                  : "text-[#c2570f]"
              }`}
            >
              @{parameter.name}
            </span>
            <span className="text-[10px] text-[var(--color-text-secondary)]">
              {parameter.label}
            </span>
          </button>
        );
      })}
    </>
  );
}
